"use client";

import { useEffect, useState } from "react";
import { Icon } from "./Icons";
import {
  ConfirmDialog,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  StatusPill,
  formatDate,
  formatMoney
} from "./UI";

const TABS = [
  ["pricing", "Pricing", "calculator"],
  ["tariffs", "Tariffs", "calendar"],
  ["promotions", "Promotions", "tag"],
  ["users", "Users", "users"],
  ["data", "Data", "download"]
];

export default function AdminPage({ data, actions, user }) {
  const [tab, setTab] = useState("pricing");
  const allowedTabs = user.role === "admin"
    ? TABS
    : TABS.filter(([id]) => !["users", "data"].includes(id));

  return (
    <div className="page">
      <PageHeader
        eyebrow="Administration"
        title="Property settings"
        text="Everything that influences your rooms, prices, taxes and staff access lives here."
      />

      <div className="admin-tabs card">
        {allowedTabs.map(([id, label, icon]) => (
          <button
            className={tab === id ? "active" : ""}
            key={id}
            onClick={() => setTab(id)}
          >
            <Icon name={icon} size={18} />
            {label}
          </button>
        ))}
      </div>

      {tab === "pricing" ? (
        <PricingSettings data={data} actions={actions} />
      ) : null}
      {tab === "tariffs" ? <Tariffs data={data} actions={actions} /> : null}
      {tab === "promotions" ? (
        <Promotions data={data} actions={actions} />
      ) : null}
      {tab === "users" && user.role === "admin" ? (
        <Users data={data} actions={actions} currentUser={user} />
      ) : null}
      {tab === "data" && user.role === "admin" ? (
        <DataAndActivity data={data} actions={actions} />
      ) : null}
    </div>
  );
}

function PricingSettings({ data, actions }) {
  const [form, setForm] = useState({ ...data.settings });
  const [saving, setSaving] = useState(false);

  useEffect(() => setForm({ ...data.settings }), [data.settings]);

  function update(key) {
    return (event) => {
      const numeric = [
        "accommodationVatRate",
        "touristTaxRate",
        "commissionRate",
        "commissionVatRate",
        "commissionVatRecoverable",
        "defaultAdults",
        "defaultChildren"
      ].includes(key);
      setForm((current) => ({
        ...current,
        [key]: numeric ? Number(event.target.value) : event.target.value
      }));
    };
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await actions.updateSettings(form);
    } catch (error) {
      actions.notify(error.message, "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="settings-stack" onSubmit={save}>
      <section className="settings-section card">
        <div className="settings-section-heading">
          <span className="settings-icon purple">
            <Icon name="rooms" />
          </span>
          <div>
            <h2>Property</h2>
            <p>General defaults used across the front desk.</p>
          </div>
        </div>
        <div className="form-grid">
          <Field label="Property name">
            <input
              value={form.propertyName}
              onChange={update("propertyName")}
              required
            />
          </Field>
          <Field label="Currency">
            <select value={form.currency} onChange={update("currency")}>
              <option value="RON">RON · Romanian leu</option>
              <option value="EUR">EUR · Euro</option>
              <option value="USD">USD · US dollar</option>
              <option value="GBP">GBP · British pound</option>
            </select>
          </Field>
          <Field label="Default adults">
            <input
              type="number"
              min="1"
              max="20"
              value={form.defaultAdults}
              onChange={update("defaultAdults")}
            />
          </Field>
          <Field label="Default children">
            <input
              type="number"
              min="0"
              max="20"
              value={form.defaultChildren}
              onChange={update("defaultChildren")}
            />
          </Field>
          <Field label="Check-in time">
            <input
              type="time"
              value={form.checkInTime}
              onChange={update("checkInTime")}
            />
          </Field>
          <Field label="Check-out time">
            <input
              type="time"
              value={form.checkOutTime}
              onChange={update("checkOutTime")}
            />
          </Field>
        </div>
      </section>

      <section className="settings-section card">
        <div className="settings-section-heading">
          <span className="settings-icon teal">
            <Icon name="calculator" />
          </span>
          <div>
            <h2>VAT and local tax</h2>
            <p>
              Current researched defaults for Romanian accommodation in Băile
              Felix.
            </p>
          </div>
        </div>
        <div className="form-grid">
          <Field
            label="Accommodation VAT"
            hint="11% from 1 August 2025 for eligible hotel accommodation."
          >
            <div className="suffix-input">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.accommodationVatRate}
                onChange={update("accommodationVatRate")}
              />
              <span>%</span>
            </div>
          </Field>
          <Field
            label="Băile Felix tourist tax"
            hint="Applied to accommodation revenue excluding VAT."
          >
            <div className="suffix-input">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.touristTaxRate}
                onChange={update("touristTaxRate")}
              />
              <span>%</span>
            </div>
          </Field>
        </div>
        <div className="inline-alert">
          <Icon name="info" />
          <span>
            The tourist tax is calculated for every night of the stay and shown
            separately to the guest.
          </span>
        </div>
      </section>

      <section className="settings-section card">
        <div className="settings-section-heading">
          <span className="settings-icon orange">
            <Icon name="tag" />
          </span>
          <div>
            <h2>Booking.com costs</h2>
            <p>Match these values to your agreement and commission invoice.</p>
          </div>
        </div>
        <div className="form-grid">
          <Field label="Booking commission">
            <div className="suffix-input">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.commissionRate}
                onChange={update("commissionRate")}
              />
              <span>%</span>
            </div>
          </Field>
          <Field label="VAT on commission service">
            <div className="suffix-input">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.commissionVatRate}
                onChange={update("commissionVatRate")}
              />
              <span>%</span>
            </div>
          </Field>
          <Field
            label="Commission VAT recoverable"
            hint="Use 100% when reverse-charge VAT is fully deductible."
          >
            <div className="suffix-input">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={form.commissionVatRecoverable}
                onChange={update("commissionVatRecoverable")}
              />
              <span>%</span>
            </div>
          </Field>
          <Field label="Commission calculation basis">
            <select
              value={form.commissionBasis}
              onChange={update("commissionBasis")}
            >
              <option value="includingVat">Accommodation including VAT</option>
              <option value="excludingVat">Accommodation excluding VAT</option>
            </select>
          </Field>
          <Field
            className="field-span-2"
            label="When several promotions are eligible"
          >
            <select
              value={form.stackingMode}
              onChange={update("stackingMode")}
            >
              <option value="best">Use the largest discount only</option>
              <option value="stack">Stack discounts sequentially</option>
            </select>
          </Field>
        </div>
      </section>

      <div className="settings-save-bar">
        <div>
          <strong>Review before saving</strong>
          <span>New calculations use these values immediately.</span>
        </div>
        <button className="button button-primary" disabled={saving}>
          <Icon name="check" size={18} />
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  );
}

const EMPTY_TARIFF = {
  name: "",
  roomId: "",
  startDate: "",
  endDate: "",
  targetNet: 466.75,
  weekendAdjustment: 0,
  minStay: 1,
  active: true
};

function Tariffs({ data, actions }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TARIFF);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [error, setError] = useState("");

  const update = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.type === "number"
          ? Number(event.target.value)
          : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function save(event) {
    event.preventDefault();
    setError("");
    try {
      if (form.endDate && form.startDate && form.endDate < form.startDate) {
        throw new Error("The end date must be after the start date.");
      }
      if (editing?.id) {
        await actions.update("tariffs", editing.id, form, "Tariff updated");
      } else {
        await actions.create("tariffs", form, "Tariff created");
      }
      setEditing(null);
    } catch (saveError) {
      setError(saveError.message);
    }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading">
        <div>
          <h2>Seasonal tariffs</h2>
          <p>
            Set the desired hotel take-home by period and optionally by room.
          </p>
        </div>
        <button
          className="button button-primary"
          onClick={() => {
            setForm(EMPTY_TARIFF);
            setEditing({ id: null });
          }}
        >
          <Icon name="plus" size={18} />
          Add tariff
        </button>
      </div>

      {data.tariffs.length ? (
        <div className="admin-card-list">
          {data.tariffs.map((tariff) => {
            const room = data.rooms.find((item) => item.id === tariff.roomId);
            return (
              <article className="admin-record card" key={tariff.id}>
                <span className="record-icon purple">
                  <Icon name="calendar" />
                </span>
                <div className="record-main">
                  <div>
                    <h3>{tariff.name}</h3>
                    <StatusPill value={tariff.active ? "active" : "inactive"} />
                  </div>
                  <p>
                    {room ? `${room.code} · ${room.name}` : "All rooms"} ·{" "}
                    {tariff.startDate ? formatDate(tariff.startDate) : "Any date"}{" "}
                    → {tariff.endDate ? formatDate(tariff.endDate) : "No end"}
                  </p>
                </div>
                <div className="record-value">
                  <small>Client nightly total</small>
                  <strong>
                    {formatMoney(tariff.targetNet, data.settings.currency)}
                  </strong>
                  {Number(tariff.weekendAdjustment) ? (
                    <span>
                      +{formatMoney(tariff.weekendAdjustment, data.settings.currency)}{" "}
                      weekends
                    </span>
                  ) : null}
                </div>
                <div className="card-actions">
                  <button
                    className="icon-button"
                    onClick={() => {
                      setForm({ ...EMPTY_TARIFF, ...tariff });
                      setEditing(tariff);
                    }}
                  >
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    className="icon-button icon-button-danger"
                    onClick={() => setRemoveTarget(tariff)}
                  >
                    <Icon name="trash" size={18} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon="calendar"
          title="No tariffs yet"
          text="Without a tariff, each room uses its default client total."
        />
      )}

      <Modal
        open={Boolean(editing)}
        title={editing?.id ? "Edit tariff" : "Add tariff"}
        eyebrow="Pricing period"
        onClose={() => setEditing(null)}
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Tariff name">
              <input
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Summer high season"
              />
            </Field>
            <Field label="Applies to">
              <select value={form.roomId} onChange={update("roomId")}>
                <option value="">All rooms</option>
                {data.rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.code} · {room.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Start date">
              <input
                type="date"
                value={form.startDate}
                onChange={update("startDate")}
              />
            </Field>
            <Field label="End date">
              <input
                type="date"
                value={form.endDate}
                onChange={update("endDate")}
              />
            </Field>
            <Field label={`Client nightly total (${data.settings.currency})`}>
              <input
                type="number"
                min="0"
                step="10"
                required
                value={form.targetNet}
                onChange={update("targetNet")}
              />
            </Field>
            <Field label={`Weekend adjustment (${data.settings.currency})`}>
              <input
                type="number"
                step="5"
                value={form.weekendAdjustment}
                onChange={update("weekendAdjustment")}
              />
            </Field>
            <Field label="Minimum stay">
              <input
                type="number"
                min="1"
                max="30"
                value={form.minStay}
                onChange={update("minStay")}
              />
            </Field>
            <label className="switch-row">
              <div>
                <strong>Active tariff</strong>
                <span>Allow the calculator to use this period.</span>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                onChange={update("active")}
              />
              <i />
            </label>
          </div>
          {error ? (
            <div className="inline-alert inline-alert-error">
              <Icon name="warning" />
              {error}
            </div>
          ) : null}
          <div className="modal-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button className="button button-primary">Save tariff</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Delete tariff?"
        text={removeTarget ? `${removeTarget.name} will be removed.` : ""}
        onClose={() => setRemoveTarget(null)}
        onConfirm={async () => {
          await actions.remove("tariffs", removeTarget.id, "Tariff deleted");
          setRemoveTarget(null);
        }}
      />
    </section>
  );
}

const EMPTY_PROMOTION = {
  name: "",
  discount: 10,
  channel: "Booking.com",
  active: true,
  notes: ""
};

function Promotions({ data, actions }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PROMOTION);
  const [removeTarget, setRemoveTarget] = useState(null);

  const update = (key) => (event) =>
    setForm((current) => ({
      ...current,
      [key]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.type === "number"
            ? Number(event.target.value)
            : event.target.value
    }));

  async function save(event) {
    event.preventDefault();
    try {
      if (editing?.id) {
        await actions.update(
          "promotions",
          editing.id,
          form,
          "Promotion updated"
        );
      } else {
        await actions.create("promotions", form, "Promotion created");
      }
      setEditing(null);
    } catch (error) {
      actions.notify(error.message, "error");
    }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading">
        <div>
          <h2>Promotion profiles</h2>
          <p>
            Add any Booking.com or direct discount and choose it in the
            calculator.
          </p>
        </div>
        <button
          className="button button-primary"
          onClick={() => {
            setForm(EMPTY_PROMOTION);
            setEditing({ id: null });
          }}
        >
          <Icon name="plus" size={18} />
          Add promotion
        </button>
      </div>

      <div className="promotion-admin-grid">
        {data.promotions.map((promotion) => (
          <article className="promotion-admin-card card" key={promotion.id}>
            <div className="promotion-percent">−{promotion.discount}%</div>
            <div>
              <span className="eyebrow">{promotion.channel}</span>
              <h3>{promotion.name}</h3>
              <p>{promotion.notes || "No additional conditions."}</p>
              <StatusPill value={promotion.active ? "active" : "inactive"} />
            </div>
            <div className="card-actions">
              <button
                className="icon-button"
                onClick={() => {
                  setForm({ ...EMPTY_PROMOTION, ...promotion });
                  setEditing(promotion);
                }}
              >
                <Icon name="edit" size={18} />
              </button>
              <button
                className="icon-button icon-button-danger"
                onClick={() => setRemoveTarget(promotion)}
              >
                <Icon name="trash" size={18} />
              </button>
            </div>
          </article>
        ))}
      </div>

      <Modal
        open={Boolean(editing)}
        title={editing?.id ? "Edit promotion" : "Add promotion"}
        eyebrow="Discount profile"
        onClose={() => setEditing(null)}
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Promotion name">
              <input
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Genius Level 1"
              />
            </Field>
            <Field label="Channel">
              <select value={form.channel} onChange={update("channel")}>
                <option>Booking.com</option>
                <option>Direct</option>
                <option>Agency</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Discount percentage">
              <div className="suffix-input">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.discount}
                  onChange={update("discount")}
                />
                <span>%</span>
              </div>
            </Field>
            <label className="switch-row">
              <div>
                <strong>Enabled by default</strong>
                <span>Preselect this promotion in the calculator.</span>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                onChange={update("active")}
              />
              <i />
            </label>
            <Field className="field-span-2" label="Conditions or notes">
              <textarea
                rows="3"
                value={form.notes}
                onChange={update("notes")}
                placeholder="Eligible rooms, booking window, guest type…"
              />
            </Field>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button className="button button-primary">Save promotion</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Delete promotion?"
        text={removeTarget ? `${removeTarget.name} will be removed.` : ""}
        onClose={() => setRemoveTarget(null)}
        onConfirm={async () => {
          await actions.remove(
            "promotions",
            removeTarget.id,
            "Promotion deleted"
          );
          setRemoveTarget(null);
        }}
      />
    </section>
  );
}

const EMPTY_USER = {
  name: "",
  email: "",
  role: "staff",
  active: true,
  password: ""
};

function Users({ data, actions, currentUser }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_USER);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [error, setError] = useState("");

  const update = (key) => (event) =>
    setForm((current) => ({
      ...current,
      [key]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value
    }));

  async function save(event) {
    event.preventDefault();
    setError("");
    try {
      if (!editing?.id && form.password.length < 8) {
        throw new Error("New users need a password of at least 8 characters.");
      }
      if (editing?.id && form.password && form.password.length < 8) {
        throw new Error("A replacement password needs at least 8 characters.");
      }
      if (editing?.id) {
        await actions.update("users", editing.id, form, "User updated");
      } else {
        await actions.create("users", form, "User created");
      }
      setEditing(null);
    } catch (saveError) {
      setError(saveError.message);
    }
  }

  return (
    <section className="admin-section">
      <div className="admin-section-heading">
        <div>
          <h2>Users and access</h2>
          <p>Create staff accounts, choose roles and reset passwords.</p>
        </div>
        <button
          className="button button-primary"
          onClick={() => {
            setForm(EMPTY_USER);
            setEditing({ id: null });
            setError("");
          }}
        >
          <Icon name="plus" size={18} />
          Create user
        </button>
      </div>

      <div className="user-list card">
        {data.users.map((account) => (
          <article className="user-row" key={account.id}>
            <span className="profile-avatar profile-avatar-large">
              {account.name?.slice(0, 1).toUpperCase()}
            </span>
            <div className="user-main">
              <div>
                <h3>{account.name}</h3>
                {account.id === currentUser.id ? (
                  <span className="you-badge">You</span>
                ) : null}
              </div>
              <p>{account.email}</p>
            </div>
            <StatusPill value={account.role} />
            <StatusPill value={account.active ? "active" : "inactive"} />
            <div className="card-actions">
              <button
                className="icon-button"
                onClick={() => {
                  setForm({ ...EMPTY_USER, ...account, password: "" });
                  setEditing(account);
                  setError("");
                }}
                aria-label={`Edit ${account.name}`}
              >
                <Icon name="edit" size={18} />
              </button>
              {account.id !== currentUser.id ? (
                <button
                  className="icon-button icon-button-danger"
                  onClick={() => setRemoveTarget(account)}
                  aria-label={`Delete ${account.name}`}
                >
                  <Icon name="trash" size={18} />
                </button>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <Modal
        open={Boolean(editing)}
        title={editing?.id ? "Edit user" : "Create user"}
        eyebrow="Account access"
        onClose={() => setEditing(null)}
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Name">
              <input
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Front desk"
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                inputMode="email"
                autoCapitalize="none"
                required
                value={form.email}
                onChange={update("email")}
                placeholder="staff@hotel.com"
              />
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={update("role")}>
                <option value="staff">Staff</option>
                <option value="admin">Administrator</option>
              </select>
            </Field>
            <Field
              label={editing?.id ? "New password (optional)" : "Temporary password"}
              hint={editing?.id ? "Leave blank to keep the current password." : ""}
            >
              <input
                type="password"
                minLength={form.password ? 8 : undefined}
                required={!editing?.id}
                autoComplete="new-password"
                value={form.password}
                onChange={update("password")}
                placeholder="At least 8 characters"
              />
            </Field>
            <label className="switch-row field-span-2">
              <div>
                <strong>Account active</strong>
                <span>Inactive users cannot sign in.</span>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                disabled={editing?.id === currentUser.id}
                onChange={update("active")}
              />
              <i />
            </label>
          </div>
          {error ? (
            <div className="inline-alert inline-alert-error">
              <Icon name="warning" />
              {error}
            </div>
          ) : null}
          <div className="modal-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button className="button button-primary">
              {editing?.id ? "Save user" : "Create user"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Delete user?"
        text={
          removeTarget
            ? `${removeTarget.name} will permanently lose access. You can deactivate the account instead if you want to keep its history.`
            : ""
        }
        onClose={() => setRemoveTarget(null)}
        onConfirm={async () => {
          await actions.remove("users", removeTarget.id, "User deleted");
          setRemoveTarget(null);
        }}
      />
    </section>
  );
}

function DataAndActivity({ data, actions }) {
  return (
    <section className="settings-stack">
      <div className="backup-card card">
        <span className="backup-illustration">
          <Icon name="download" size={34} />
        </span>
        <div>
          <span className="eyebrow">Portable backup</span>
          <h2>Export your hotel data</h2>
          <p>
            Download settings, rooms, reservations, tariffs and promotions as
            JSON. Password hashes are never included.
          </p>
        </div>
        <button className="button button-primary" onClick={actions.exportData}>
          <Icon name="download" size={18} />
          Download backup
        </button>
      </div>

      <div className="mode-detail card">
        <span className={`mode-detail-icon ${data.mode}`}>
          <Icon name={data.mode === "cloud" ? "cloud" : "device"} size={26} />
        </span>
        <div>
          <h3>
            {data.mode === "cloud"
              ? "Cloud database connected"
              : "Data is stored on this device"}
          </h3>
          <p>
            {data.mode === "cloud"
              ? "Rooms, reservations and users synchronize between authorized devices."
              : "This deployment is fully usable offline. Connect the free D1 backend to synchronize two users across devices."}
          </p>
        </div>
      </div>

      <section className="settings-section card">
        <div className="settings-section-heading">
          <span className="settings-icon purple">
            <Icon name="availability" />
          </span>
          <div>
            <h2>Recent activity</h2>
            <p>The latest changes made inside the app.</p>
          </div>
        </div>
        {data.activity?.length ? (
          <div className="activity-list">
            {data.activity.slice(0, 20).map((event) => (
              <div key={event.id}>
                <span className="activity-dot" />
                <div>
                  <strong>{event.detail}</strong>
                  <span>
                    {event.userName} ·{" "}
                    {new Intl.DateTimeFormat("en-GB", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit"
                    }).format(new Date(event.at))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">No activity recorded yet.</p>
        )}
      </section>
    </section>
  );
}
